import { useTranslation } from 'react-i18next';
import { MemberCardTemplateEditor } from './MemberCardTemplateEditor';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MemberCenterAppearanceResponse,
  MemberCenterAppearanceV1,
  MemberCenterMetric,
} from '@pokerroom/types';
import { DEFAULT_MEMBER_CENTER_APPEARANCE } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import './MemberPortalEditorPage.css';

const metrics: { key: MemberCenterMetric; label: string; sample: string }[] = [
  { key: 'rewardPoints', label: 'preview.playerPoints', sample: '1,280' },
  { key: 'prizeCredits', label: 'preview.credits', sample: '300' },
];

export default function MemberPortalEditorPage() {
  const { t } = useTranslation('member');
  const actions = useAuthStore((s) => s.allowedActions);
  const canManage = actions?.includes('member:portal_manage') ?? false;
  const { venue } = useCurrentVenue();
  const currentVenue = useRef(venue?.id);
  currentVenue.current = venue?.id;
  const queryClient = useQueryClient();
  const key = ['member-center-appearance', venue?.id];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => api.get<MemberCenterAppearanceResponse>('/members/portal-appearance'),
    enabled: canManage && Boolean(venue?.id),
    retry: false,
  });
  const [draft, setDraft] = useState<MemberCenterAppearanceV1>(DEFAULT_MEMBER_CENTER_APPEARANCE);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setSaving(false);
    if (data) {
      setDraft(data.appearance);
      setMessage('');
    }
  }, [data, venue?.id]);
  const changed = Boolean(data && JSON.stringify(draft) !== JSON.stringify(data.appearance));
  const setText = (field: 'heading' | 'cardCaption' | 'supportNote', value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setMessage('');
  };
  const toggle = (metric: MemberCenterMetric) => {
    setDraft((prev) => ({
      ...prev,
      visibleMetrics: prev.visibleMetrics.includes(metric)
        ? prev.visibleMetrics.filter((item) => item !== metric)
        : [...prev.visibleMetrics, metric],
    }));
    setMessage('');
  };
  const move = (metric: MemberCenterMetric, direction: -1 | 1) => {
    setDraft((prev) => {
      const next = [...prev.visibleMetrics];
      const index = next.indexOf(metric);
      const other = index + direction;
      if (index < 0 || other < 0 || other >= next.length) return prev;
      [next[index], next[other]] = [next[other]!, next[index]!];
      return { ...prev, visibleMetrics: next };
    });
  };
  async function save(next: MemberCenterAppearanceV1) {
    if (!data || saving) return;
    const savingVenue = venue?.id;
    setSaving(true);
    setMessage('');
    try {
      const saved = await api.put<MemberCenterAppearanceResponse>('/members/portal-appearance', {
        appearance: next,
        revision: data.revision,
      });
      queryClient.setQueryData(key, saved);
      if (currentVenue.current !== savingVenue) return;
      setDraft(saved.appearance);
      setMessage(t('portalEditor.saved'));
    } catch (error) {
      if (currentVenue.current !== savingVenue) return;
      const status = (error as { response?: { status?: number } }).response?.status;
      setMessage(status === 409 ? t('portalEditor.conflict') : t('portalEditor.saveError'));
    } finally {
      if (currentVenue.current === savingVenue) setSaving(false);
    }
  }
  if (!actions) return <div className="member-editor-page">{t('portalEditor.checking')}</div>;
  if (!canManage)
    return (
      <div className="member-editor-page" role="alert">
        {t('portalEditor.denied')}
      </div>
    );
  if (!venue || isLoading)
    return <div className="member-editor-page">{t('portalEditor.loading')}</div>;
  if (isError || !data)
    return (
      <div className="member-editor-page" role="alert">
        {t('portalEditor.loadError')}
      </div>
    );
  return (
    <main className="member-editor-page">
      <p className="member-editor-label">MEMBER CENTER / EDITOR</p>
      <h1>{t('portalEditor.title')}</h1>
      <p>{t('portalEditor.context', { venue: venue.name })}</p>
      {message && (
        <p role="status" className="member-editor-message">
          {message}
        </p>
      )}
      <MemberCardTemplateEditor key={venue.id} venueId={venue.id} appearance={draft} />
      <div className="member-editor-grid">
        <section className="member-editor-controls" aria-label={t('portalEditor.controls')}>
          <h2>{t('portalEditor.text')}</h2>
          <label>
            {t('portalEditor.heading')}
            <input
              value={draft.heading}
              maxLength={40}
              onChange={(e) => setText('heading', e.target.value)}
            />
          </label>
          <label>
            {t('portalEditor.caption')}
            <input
              value={draft.cardCaption}
              maxLength={40}
              onChange={(e) => setText('cardCaption', e.target.value)}
            />
          </label>
          <label>
            {t('portalEditor.note')}
            <textarea
              value={draft.supportNote}
              maxLength={160}
              onChange={(e) => setText('supportNote', e.target.value)}
            />
          </label>
          <h2>{t('portalEditor.metrics')}</h2>
          <p>{t('portalEditor.fixed')}</p>
          {metrics.map(({ key: metric, label }) => (
            <div className="member-editor-metric" key={metric}>
              <label>
                <input
                  type="checkbox"
                  checked={draft.visibleMetrics.includes(metric)}
                  onChange={() => toggle(metric)}
                />
                {t(label)}
              </label>
              <div>
                <button
                  type="button"
                  aria-label={`${t(label)}上移`}
                  disabled={!draft.visibleMetrics.includes(metric)}
                  onClick={() => move(metric, -1)}
                >
                  {t('portalEditor.up')}
                </button>
                <button
                  type="button"
                  aria-label={`${t(label)}下移`}
                  disabled={!draft.visibleMetrics.includes(metric)}
                  onClick={() => move(metric, 1)}
                >
                  {t('portalEditor.down')}
                </button>
              </div>
            </div>
          ))}
          <div className="member-editor-actions">
            <button
              className="member-editor-save"
              disabled={!changed || saving || !draft.heading.trim() || !draft.cardCaption.trim()}
              onClick={() => void save(draft)}
            >
              {saving ? t('portalEditor.saving') : t('portalEditor.save')}
            </button>
            <button
              disabled={!changed || saving}
              onClick={() => {
                setDraft(data.appearance);
                setMessage('');
              }}
            >
              {t('portalEditor.cancel')}
            </button>
            <button
              disabled={saving}
              onClick={() => {
                if (window.confirm(t('portalEditor.confirmReset')))
                  void save(DEFAULT_MEMBER_CENTER_APPEARANCE);
              }}
            >
              {t('portalEditor.reset')}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
